import React, { useEffect, useMemo, useState } from 'react'
import { RiskAnalysis, RiskAnalysisStatus, SWOT } from '../../../api/schemas/schema'
import { TypeFilter } from './TypeFilter'
import { Button, makeStyles, Switch, tokens } from '@fluentui/react-components'
import { ArrowCircleDownUp20Regular } from '@fluentui/react-icons'
import { t } from '../../../i18n/i18n'
import { ModalSWOTAnalyseEvaluate } from './ModalSWOTAnalyseEvaluate'
import { PhoneHeader } from '../../common/header/PhoneHeader'
import { ModalSWOTChangeOrder } from './ModalSWOTChangeOrder'
import { SWOTAnalyseEvaluateList } from './SWOTAnalyseEvaluateList'
import { IItemState } from '../../../interfaces/IItemState'

const useStyles = makeStyles({
   flexRight: {
      justifyContent: 'flex-end',
      '& > *': {
         marginRight: tokens.spacingHorizontalXS,
         marginLeft: tokens.spacingHorizontalXS,
      },
   },
   hideInPhone: {
      '@media screen and (max-width: 640px)': {
         display: 'none',
      },
   },
})

type SWOTAnalyseEvaluateProps = {
   analysisState: IItemState<RiskAnalysis>
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
}

export const SWOTAnalyseEvaluate = ({ analysisState, setRiskAnalysisState }: SWOTAnalyseEvaluateProps) => {
   const classes = useStyles()
   const [modals, setModals] = useState({
      determineModal: false,
      changeOrderModal: false,
      evaluateModal: false,
   })
   const [graphView, setGraphView] = useState<boolean>(false)
   const [selectedSwotId, setSelectedSwotId] = useState<string>(null)
   const [disabled, setDisabled] = useState<boolean>(false)
   const [checkboxHasLocalState, setCheckboxHasLocalState] = useState<boolean>(false)
   const [manualIsDirty, setManualIsDirty] = useState<boolean>(false)

   const analysis = analysisState.item

   useEffect(() => {
      if (analysis.swoTs.length < 1 || analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed) setDisabled(true)
   }, [analysis, analysisState.disabled])

   const sortedSwots = useMemo(() => [...(analysis.swoTs ?? [])].sort((a, b) => a.order - b.order), [analysis.swoTs])
   const sortedAnalysis = useMemo(() => ({ ...analysis, swoTs: sortedSwots }), [analysis, sortedSwots])

   const selectedSwot = useMemo(() => sortedSwots.find((swot) => swot.id === selectedSwotId) ?? null, [sortedSwots, selectedSwotId])
   const setSelectedSwot = (swot: SWOT) => setSelectedSwotId(swot?.id ?? null)

   const toggleModal = (modalName: string, isOpen: boolean) => {
      setModals({
         determineModal: false,
         changeOrderModal: false,
         evaluateModal: false,
         [modalName]: isOpen,
      })
   }

   const toggleCheckbox = (swot: SWOT, openModal?: boolean) => {
      swot = { ...swot, actionRequired: !swot.actionRequired }

      const updatedSwots = analysis.swoTs.map((item) => (item.id === swot.id ? swot : item))

      const updatedAnalysis = { ...analysis, swoTs: updatedSwots }
      setRiskAnalysisState((prev) => ({
         ...prev,
         item: updatedAnalysis,
      }))
      openModal && setCheckboxHasLocalState(true)
      return swot
   }

   const title = t('AnalyseEvaluate')
   return (
      <>
         <PhoneHeader title={title} action={null} />

         <TypeFilter
            title={title}
            disabled={graphView}
            action={
               <div className={classes.flexRight}>
                  <Button
                     onClick={() => {
                        toggleModal('changeOrderModal', true)
                     }}
                     icon={<ArrowCircleDownUp20Regular />}
                     disabled={disabled || graphView}
                  >
                     {t('ChangeOrder')}
                  </Button>
               </div>
            }
            optionalButton={
               <Switch
                  label={t('Graph')}
                  onChange={() => {
                     setGraphView(!graphView)
                  }}
                  className={classes.hideInPhone}
                  disabled={analysis.swoTs.length === 0}
               />
            }
         />

         <SWOTAnalyseEvaluateList
            analysis={sortedAnalysis}
            disabled={disabled}
            graphView={graphView}
            toggleModal={toggleModal}
            toggleCheckbox={toggleCheckbox}
            setSelectedSwot={setSelectedSwot}
            setManualIsDirty={setManualIsDirty}
         />

         {selectedSwot && (
            <ModalSWOTAnalyseEvaluate
               selectedSwot={selectedSwot}
               analysis={analysis}
               disabled={disabled || (selectedSwot.determined && selectedSwot.proposedAction !== null)}
               open={modals.evaluateModal}
               checkboxHasLocalState={checkboxHasLocalState}
               close={() => {
                  toggleModal('evaluateModal', false)
                  setCheckboxHasLocalState(false)
                  setManualIsDirty(false)
               }}
               toggleCheckbox={toggleCheckbox}
               setRiskAnalysisState={setRiskAnalysisState}
               manualIsDirty={manualIsDirty}
            />
         )}

         <ModalSWOTChangeOrder
            open={modals.changeOrderModal}
            close={() => {
               toggleModal('changeOrderModal', false)
            }}
            analysis={analysis}
            setRiskAnalysisState={setRiskAnalysisState}
            items={[...sortedSwots].sort((a, b) => a.type - b.type)}
         />
      </>
   )
}
